import { Columns, Skeleton, useAviaryTheme } from "@fullscript/aviary-web";
import { useTranslation } from "react-i18next";

import { Spacer } from "@aviary";
import { SvgFullscriptLogo, SvgFullscriptLogoDarkTextGreenLeaf } from "@shared/assets/logo";
import SignInAppTestDesktop from "@shared/assets/signin-asset.png";
import { SkeletonGoogleSSOButton } from "@shared/components/LoginOptions/GoogleSSOButton/SkeletonGoogleSSOButton";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { useFlippers } from "@shared/hooks/useFlippers/useFlippers";
import { SkeletonSignInForm } from "@unauthenticated/shared/components/SignInForm/SkeletonSignInForm";
import { SignInWrapper } from "@unauthenticated/shared/components/SignInWrapper/SignInWrapper";
import { CountrySelect } from "@unauthenticated/signupSignin/components/CountrySelect/CountrySelect";
import { SignInLayout } from "@unauthenticated/signupSignin/components/SignInLayout/SignInLayout";
import { l } from "@unauthenticated/signupSignin/locales/i18n";

import * as styles from "./SignInPage.styles";

const SkeletonSignInPage = () => {
  const { t } = useTranslation();
  const { tablet } = useBreakpoints();
  const { currentTheme } = useAviaryTheme();
  const [isQualityProgramLoginEnabled] = useFlippers("platypus_quality_story_signup");
  const isMobile = !tablet.greaterThan;
  const isDarkMode = currentTheme.theme === "dark";
  const FullscriptLogo = isDarkMode ? SvgFullscriptLogo : SvgFullscriptLogoDarkTextGreenLeaf;

  const renderSignInForm = () => (
    <SignInWrapper>
      <Skeleton variant="typography" typographyType="h1" />
      <Spacer height="one" />
      <SkeletonGoogleSSOButton isOrTextRendered />
      <Spacer height="one" />
      <SkeletonSignInForm />
      <Spacer height="doubleHalf" />
      <Skeleton variant="typography" typographyType="body" isFullWidth />
    </SignInWrapper>
  );

  const renderContent = () => {
    if (isMobile) {
      return renderSignInForm();
    }

    if (!isQualityProgramLoginEnabled) {
      return (
        <Columns>
          <Columns.Column columnWidth={6}>
            <div css={styles.centeredContentContainer}>
              <img
                src={SignInAppTestDesktop}
                alt={t(l.signIn.signInPage.appBannerAlt)}
                css={styles.bannerImage}
              />
            </div>
          </Columns.Column>
          <Columns.Column columnWidth={6}>
            <div css={styles.centeredContentContainer}>{renderSignInForm()}</div>
          </Columns.Column>
        </Columns>
      );
    }

    return (
      <div css={styles.fullBleedRow}>
        <div css={styles.panelColumn}>
          <Skeleton customCss={styles.panelPlaceholder} />
        </div>
        <div css={styles.formColumn}>
          <div css={styles.formColumnInner}>
            <div css={styles.countrySelectRow}>
              <CountrySelect />
            </div>
            <div css={styles.formContent}>
              <div css={styles.formGroup}>
                <div css={styles.logoContainer}>
                  <FullscriptLogo
                    css={styles.logo}
                    data-testid="fullscript-logo"
                    aria-label={t(l.aviary.sidebarNav.fullscriptLogo)}
                  />
                </div>
                {renderSignInForm()}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return <SignInLayout isFullBleed={isQualityProgramLoginEnabled}>{renderContent()}</SignInLayout>;
};

export { SkeletonSignInPage };
